import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import useUserData from 'hooks/useUserData';
import {
  BASE,
  FORGOT_PASSWORD,
  LOGIN,
  PROCORE_SIGN_IN,
  SIGNUP,
  SSO_SIGN_IN,
  HOME,
} from 'constants/routes';
import useAuthStore from 'store/useAuthStore';
import { shallow } from 'zustand/shallow';
import PageNotFound from './PageNotFound';
import SSOSignIn from 'SSOSignIn';
import ProcoreSignIn from '@buildwise/pages/ProcoreSignIn';
import BuildWiseLoader from './BuildWiseLoader';
import Loader from 'components/common/loader/Loader';
import { handlePageScrollPosition } from 'utility/helpers';
import { useCompaniesQuery } from 'hooks/useCompanyQueries';
import { toast } from 'react-toastify';
import { CONFIGS, useConfigQuery } from '@buildwise/libs/configurations';

const Login = lazy(() => import('@buildwise/pages/Login'));
const Signup = lazy(() => import('@buildwise/pages/Signup'));
const ForgotPassword = lazy(() => import('@buildwise/pages/ForgotPassword'));
const SelectCompanyModal = lazy(() => import('components/main/SelectCompanyModal'));

const AuthRoutes = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [userAuthDetails, setUser] = useAuthStore((state) => [state.user, state.setUser], shallow);
  const isNovaAdminOrAssociatedUser = useMemo(
    () => !!(userAuthDetails?.isNovaAdmin || userAuthDetails?.isAssociatedWithOtherCompanies),
    [userAuthDetails?.isNovaAdmin, userAuthDetails?.isAssociatedWithOtherCompanies],
  );
  const [selectCompanyModal, setSelectCompanyModal] = useState(
    !userAuthDetails?.selectedCompanyId && isNovaAdminOrAssociatedUser,
  );
  const {
    companies,
    isLoading: companiesLoading,
    isFetched: isCompaniesFetched,
    isError: isCompaniesError,
  } = useCompaniesQuery({
    isAssociatedWithOtherCompanies: !userAuthDetails?.isNovaAdmin,
    shouldFetch: !!userAuthDetails?.companyId,
  });
  const [fetchUserDetails, setFetchUserDetails] = useState(false);
  const [fetchCompaniesDetails, setFetchCompanyDetails] = useState(false);
  const { user, userInfo, company, companyId, companyQueryInfo, logout } = useUserData({
    shouldFetchCompanyDetails:
      (!companiesLoading &&
        companies.length === 0 &&
        (!isNovaAdminOrAssociatedUser || !!userAuthDetails?.selectedCompanyId)) ||
      fetchCompaniesDetails,
    shouldFetchUserDetails:
      (!companiesLoading && companies.length === 0 && !isNovaAdminOrAssociatedUser) ||
      fetchUserDetails,
  });
  /* 
    Instead of fetching this after GET /company & /user calls. Fetch this concurrently.
    This config is used in MainWrapper to determine if we need to prompt User Form to complete profile.
  */
  useConfigQuery({
    companyId: companyId,
    configName: CONFIGS.USER_INPUT_FIELDS.configName,
    shouldFetch: !!company,
  });

  useEffect(() => {
    if (
      userAuthDetails &&
      (!userAuthDetails?.selectedCompanyId || isNovaAdminOrAssociatedUser || companies?.length)
    ) {
      if (companies.length > 1) {
        setSelectCompanyModal(true);
      } else if (companies.length === 1) {
        if (
          !(
            userAuthDetails?.isAssociatedWithOtherCompanies ||
            companies?.[0]?.settings?.subscriptionSettings?.allowUserAccessRequests
          ) ||
          companies?.[0]?.hasAccess
        ) {
          handleSelectCompany(companies?.[0]?.id);
        } else {
          setSelectCompanyModal(true);
        }
      } else if (
        isNovaAdminOrAssociatedUser &&
        isCompaniesFetched &&
        !isCompaniesError &&
        companies.length === 0
      ) {
        toast.error(
          'Your workspace seems to be deactivated. Please check with your company admin or contact our support team at support@buildwise.ai',
        );
      }
    }
  }, [userAuthDetails?.selectedCompanyId, isNovaAdminOrAssociatedUser, companies]);

  useEffect(() => {
    if (company) {
      setFetchUserDetails(true);
    }
  }, [company]);

  const handleSelectCompany = useCallback(
    (companyId: string) => {
      if (companyId) {
        setUser({ ...userAuthDetails, companyId, selectedCompanyId: companyId });
        setFetchCompanyDetails(true);
        if (userAuthDetails?.isNovaAdmin) {
          setFetchUserDetails(true);
        }
        setSelectCompanyModal(false);
      }
    },
    [userAuthDetails],
  );

  const signOut = useCallback(() => {
    handlePageScrollPosition('all', 0, 0, 'reset');
    logout();
    setSelectCompanyModal(false);
    navigate('/login');
  }, []);

  if (userInfo.isLoading || companyQueryInfo?.isLoading || companiesLoading) {
    return <BuildWiseLoader />;
  }

  if (company && user && pathname !== PROCORE_SIGN_IN) {
    return <Navigate to={HOME} state={{ justLoggedIn: true }} />;
  }

  return (
    <Suspense fallback={<Loader variant="fixed" />}>
      <Routes>
        <Route path={LOGIN} element={<Login />} />
        <Route path={SIGNUP} element={<Signup />} />
        <Route path={FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route path={PROCORE_SIGN_IN} element={<ProcoreSignIn />} />
        <Route path={SSO_SIGN_IN} element={<SSOSignIn />} />
        <Route path={BASE} element={<Login />} />
        <Route path="/*" element={<PageNotFound />} />
      </Routes>

      {selectCompanyModal && (
        <SelectCompanyModal
          selectCompanyModal={selectCompanyModal}
          handleSelectCompany={handleSelectCompany}
          onClose={signOut}
        />
      )}
    </Suspense>
  );
};

export default AuthRoutes;
